/** A category template's answers over time: one line per category, period by
 *  period, for the date range above. The categories with the most answers get
 *  their own line in the chart language's fixed colour order (at most seven);
 *  the rest are one grey "Other" line. Straight segments between periods (a
 *  curve would invent values between them), solid hairline grid, one value
 *  axis. In Hebrew the chart mirrors like the page: periods run from the
 *  right, the value axis stands at the right. The legend is always there, and
 *  a period's numbers show on hover. */
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useLanguage } from '@/hooks/useLanguage';
import { activeLocale, datePattern } from '@/utils/locale';
import type { TagTimeseries } from '../../types/insights';
import { axisDirection, categoryColours, formatCount, formatShare } from '../../utils/chartModel';
import { useChartTheme } from '../../utils/chartTheme';
import { useChartColors } from '../../utils/useChartColors';

export const TREND_SERIES = 7;
const OTHER = '__other__';

interface Props {
  data: TagTimeseries | null;
  loading: boolean;
  error: string | null;
  large?: boolean;
  /** A category's stored value → how the chart names it. */
  nameOf: (value: string) => string;
  /** The template's lasting category order: a category's line has the colour of its bar. */
  order?: string[];
}

interface Series { key: string; name: string; color: string; total: number; counts: number[] }

export function TemplateTrend({ data, loading, error, large, nameOf, order }: Props) {
  const { t, direction } = useLanguage();
  const colors = useChartColors();
  const chrome = useChartTheme();
  const [share, setShare] = useState(false);
  const locale = activeLocale();

  const series = useMemo<Series[]>(() => {
    if (!data?.series?.length) return [];
    const ranked = data.series
      .map((s) => ({ key: s.tag, counts: s.counts, total: s.counts.reduce((a, b) => a + b, 0) }))
      .sort((a, b) => Number(a.key === OTHER) - Number(b.key === OTHER) || b.total - a.total);
    const own = ranked.filter((s) => s.key !== OTHER).slice(0, Math.min(TREND_SERIES, colors.categorical.length - 1));
    const fills = categoryColours(own.map((s) => s.key), order, colors.categorical);
    const out: Series[] = own.map((s) => ({ ...s, name: nameOf(s.key), color: fills.get(s.key) as string }));
    const folded = ranked.filter((s) => !own.includes(s));
    if (folded.length) {
      const counts = data.buckets.map((_, bi) => folded.reduce((sum, s) => sum + (s.counts[bi] || 0), 0));
      out.push({ key: OTHER, name: t('insights.tpl.chart.other'), color: colors.other, counts,
                 total: counts.reduce((a, b) => a + b, 0) });
    }
    return out;
  }, [data, colors, nameOf, t, order]);

  // One point per period; the plotted value is a count or the period's share,
  // the tooltip always reads the counts themselves (`index`).
  const points = useMemo(() => (data?.buckets || []).map((bucket, index) => {
    const whole = series.reduce((sum, s) => sum + (s.counts[index] || 0), 0);
    const point: Record<string, number | string> = { index, label: dayjs(bucket).format(datePattern('monthDay')) };
    series.forEach((s) => {
      const n = s.counts[index] || 0;
      point[s.key] = share ? (whole ? (n / whole) * 100 : 0) : n;
    });
    return point;
  }), [data, series, share]);

  if (loading && !data) return <div className="tpl-skel tpl-skel--chart" aria-busy="true" />;
  if (error) return <p className="tpl-error" role="alert">{error}</p>;
  if (!data || !data.buckets.length || !series.length) {
    return <p className="tpl-muted tpl-trend__empty">{t('insights.noTrendData')}</p>;
  }

  const axis = axisDirection(direction === 'rtl');
  const tick = { fontSize: 11, fill: chrome.axisText };
  return (
    <div className={`tpl-trend${loading ? ' is-refreshing' : ''}`}>
      <div className="tpl-trend__top">
        <ul className="tpl-trend__legend" aria-label={t('insights.tpl.chart.legend')}>
          {series.map((s) => (
            <li key={s.key}>
              <span className="tpl-trend__key" title={s.name}>
                <span className="tpl-swatch" style={{ background: s.color }} aria-hidden="true" />
                <span className="tpl-trend__name" dir="auto">{s.name}</span>
                <span className="tpl-trend__total"><bdi>{formatCount(s.total, locale)}</bdi></span>
              </span>
            </li>
          ))}
        </ul>
        <span className="tpl-views" role="group" aria-label={t('insights.tpl.chart.trendAs')}>
          <button type="button" className={`cx-btn cx-btn--sm${share ? '' : ' is-on'}`} aria-pressed={!share}
                  onClick={() => setShare(false)}>{t('insights.tpl.chart.trendCount')}</button>
          <button type="button" className={`cx-btn cx-btn--sm${share ? ' is-on' : ''}`} aria-pressed={share}
                  onClick={() => setShare(true)}>{t('insights.tpl.chart.trendShare')}</button>
        </span>
      </div>
      <div className="tpl-trend__plot" role="img"
           aria-label={t('insights.tpl.chart.trendAlt', { n: series.length, weeks: data.buckets.length })}>
        <ResponsiveContainer width="100%" height={large ? 360 : 260}>
          <LineChart data={points} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid stroke={chrome.gridLine} vertical={false} />
            <XAxis dataKey="label" reversed={axis.reversed} tick={tick} tickLine={false}
                   axisLine={{ stroke: chrome.xAxisLine }} minTickGap={16} />
            <YAxis orientation={axis.valueAxisSide} tick={tick} tickLine={false} axisLine={false} width={40}
                   allowDecimals={false}
                   tickFormatter={(v: number) => (share ? formatShare(v, 100, locale) : formatCount(v, locale))} />
            <Tooltip
              cursor={{ stroke: chrome.xAxisLine }}
              content={({ active, payload, label }) => {
                const index = Number(payload?.[0]?.payload?.index);
                if (!active || !Number.isFinite(index)) return null;
                const whole = series.reduce((sum, s) => sum + (s.counts[index] || 0), 0);
                return (
                  <div className="tpl-tip" dir={direction}>
                    <div className="tpl-tip__title">{t('insights.tpl.chart.weekOf', { date: label })}</div>
                    {series.map((s) => (
                      <div key={s.key} className="tpl-tip__row">
                        <span className="tpl-swatch" style={{ background: s.color }} aria-hidden="true" />
                        <span className="tpl-tip__name" dir="auto">{s.name}</span>
                        <bdi className="tpl-tip__num">
                          {formatCount(s.counts[index] || 0, locale)} · {formatShare(s.counts[index] || 0, whole, locale)}
                        </bdi>
                      </div>
                    ))}
                  </div>
                );
              }}
            />
            {series.map((s) => (
              <Line key={s.key} type="linear" dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2}
                    dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: chrome.cardBg }} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="tpl-chart__note">{t('insights.tpl.chart.trendNote')}</p>
    </div>
  );
}
